import React, { useState, useEffect } from 'react';
import { 
  Home, 
  FileText, 
  Users, 
  BarChart3, 
  Zap, 
  Palette, 
  Settings,
  Menu, 
  X, 
  User, 
  LogOut,
  ChevronDown,
  Bell,
  ChevronRight
} from 'lucide-react';
import { ViewType } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import NotificationCenter from '../Notifications/NotificationCenter';
import { useNotifications } from '../../contexts/NotificationContext';
import ThemeToggle from '../Theme/ThemeToggle';

interface MobileNavigationProps {
  currentView: ViewType;
  onViewChange: (view: ViewType) => void;
}

const MobileNavigation: React.FC<MobileNavigationProps> = ({ currentView, onViewChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const { currentUser, logout } = useAuth();
  const { notifications, unreadCount } = useNotifications();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && !(event.target as Element).closest('.mobile-nav')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);


  const navigationItems = [
    { 
      id: 'dashboard' as ViewType, 
      label: 'Dashboard', 
      icon: Home, 
      description: 'Overview & stats' 
    },
    { 
      id: 'content' as ViewType, 
      label: 'Content', 
      icon: FileText, 
      description: 'Posts, templates & media',
      subItems: [
        { id: 'posts' as ViewType, label: 'Posts' },
        { id: 'templates' as ViewType, label: 'Templates' },
        { id: 'media' as ViewType, label: 'Media' }
      ]
    },
    { 
      id: 'audience' as ViewType, 
      label: 'Audience', 
      icon: Users, 
      description: 'Subscribers & segments',
      subItems: [
        { id: 'subscribers' as ViewType, label: 'Subscribers' },
        { id: 'segments' as ViewType, label: 'Segments' },
        { id: 'import' as ViewType, label: 'Import/Export' }
      ]
    },
    { 
      id: 'insights' as ViewType, 
      label: 'Insights', 
      icon: BarChart3, 
      description: 'Analytics & revenue',
      subItems: [
        { id: 'analytics' as ViewType, label: 'Analytics' },
        { id: 'revenue' as ViewType, label: 'Revenue' },
        { id: 'reports' as ViewType, label: 'Reports' }
      ]
    },
    { 
      id: 'automation' as ViewType, 
      label: 'Automation', 
      icon: Zap, 
      description: 'Campaigns & triggers',
      subItems: [
        { id: 'campaigns' as ViewType, label: 'Campaigns' },
        { id: 'triggers' as ViewType, label: 'Triggers' }
      ]
    },
    { 
      id: 'brand' as ViewType, 
      label: 'Brand', 
      icon: Palette, 
      description: 'Themes & customization',
      subItems: [
        { id: 'themes' as ViewType, label: 'Themes' },
        { id: 'brand-settings' as ViewType, label: 'Brand Settings' }
      ]
    },
    { 
      id: 'account' as ViewType, 
      label: 'Account', 
      icon: Settings, 
      description: 'Billing & settings',
      subItems: [
        { id: 'billing' as ViewType, label: 'Billing' },
        { id: 'settings' as ViewType, label: 'Settings' },
        { id: 'team' as ViewType, label: 'Team' }
      ]
    }
  ];

  const handleLogout = async () => {
    try {
      await logout();
      setIsOpen(false);
      setShowUserMenu(false);
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleViewChange = (view: ViewType) => {
    onViewChange(view);
    setIsOpen(false);
  };


  return (
    <>
      {/* Mobile Header */}
      <header className="lg:hidden bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50 shadow-sm">
        <div className="px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">P</span>
            </div>
            <div>
              <span className="text-lg font-bold text-gray-900 dark:text-gray-100">PostSub.io</span>
              <div className="text-xs text-gray-500 dark:text-gray-400">Post-Substack Era</div>
            </div>
          </div>
          
              <div className="flex items-center gap-2">
                {/* Theme Toggle */}
                <ThemeToggle size="sm" />
                
                {/* Notifications */}
                <button
                  onClick={() => setShowNotifications(true)}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors relative touch-manipulation"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
            
            {/* User Menu */}
            <div className="relative mobile-nav">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors touch-manipulation min-h-[44px]"
              >
                <div className="w-6 h-6 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4" />
                </div>
                <ChevronDown className={`w-3 h-3 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50 mobile-nav">
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{currentUser?.displayName || 'User'}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{currentUser?.email}</div>
                  </div>
                  <div className="py-1">
                    <button 
                      onClick={() => {
                        // Navigate to Account Hub settings tab
                        onViewChange('account');
                        setShowUserMenu(false);
                        setIsOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors touch-manipulation min-h-[44px]"
                  data-testid="touch-friendly"
                    >
                      <Settings className="w-4 h-4" />
                      Settings
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors touch-manipulation min-h-[44px]"
                      data-testid="touch-friendly"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Hamburger Menu */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <div className="relative w-5 h-5">
                <Menu className={`w-5 h-5 absolute transition-all duration-300 ${isOpen ? 'opacity-0 rotate-90' : 'opacity-100 rotate-0'}`} />
                <X className={`w-5 h-5 absolute transition-all duration-300 ${isOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'}`} />
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-0 right-0 w-80 h-full bg-white dark:bg-gray-800 shadow-2xl">
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Menu</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Navigate your platform</p>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-colors touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Navigation Items */}
              <div className="flex-1 overflow-y-auto p-6">
                <nav className="space-y-2">
                  {navigationItems.map((item, index) => {
                    const Icon = item.icon;
                    const isActive = currentView === item.id || 
                      (item.subItems && item.subItems.some(sub => currentView === sub.id));
                    
                    return (
                      <div key={item.id} style={{ animationDelay: `${index * 50}ms` }}>
                        <button
                          onClick={() => handleViewChange(item.id)}
                          className={`w-full flex items-center justify-between gap-4 px-4 py-4 rounded-xl text-left transition-all duration-200 transform hover:scale-[1.02] touch-manipulation min-h-[56px] ${
                            isActive
                              ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-sm'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`p-2 rounded-lg ${isActive ? 'bg-white bg-opacity-20' : 'bg-gray-100 dark:bg-gray-600'}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1">
                              <span className="font-medium">{item.label}</span>
                              <div className="text-xs opacity-75 mt-1">
                                {item.description}
                              </div>
                            </div>
                          </div>
                          {item.subItems && (
                            <ChevronRight className={`w-4 h-4 transition-transform ${
                              isActive ? 'rotate-90' : ''
                            }`} />
                          )}
                        </button>
                        
                        {/* Sub-items */}
                        {item.subItems && isActive && (
                          <div className="ml-8 mt-2 space-y-1">
                            {item.subItems.map((subItem) => (
                              <button
                                key={subItem.id}
                                onClick={() => handleViewChange(subItem.id)}
                                className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-colors touch-manipulation min-h-[44px] ${
                                  currentView === subItem.id
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                              >
                                {subItem.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </nav>

                {/* Platform Info Card */}
                <div className="mt-8 p-6 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl text-white">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold">Platform Fee</div>
                    <div className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded-full">LOWEST</div>
                  </div>
                  <div className="text-3xl font-bold mb-2">5%</div>
                  <div className="text-sm opacity-90 mb-2">vs Substack's 10%</div>
                  <div className="text-sm font-medium text-green-200">Save $117/month on average</div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">PostSub.io v1.0</div>
                  <div className="text-xs text-gray-400 dark:text-gray-500">The post-Substack platform</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Center */}
      <NotificationCenter
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />

    </>
  );
};

export default MobileNavigation;